import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import {
    HostApiService,
    LastKnownProductService,
    LocalStoreKey,
    LocalStoreService,
    NavigationService,
    Page,
    WebWorkerService,
    WorkerType,
} from '@frontend/vanilla/core';
import { firstValueFrom } from 'rxjs';

import { HostAppConfig } from '../provide';
import { MaintenanceConfig } from './maintenance.model';

@Injectable({
    providedIn: 'root',
})
export class MaintenanceService {
    private http = inject(HostApiService);
    private lastKnownProductService = inject(LastKnownProductService);
    private config = inject(HostAppConfig);
    private page = inject(Page);
    private localStorage = inject(LocalStoreService);
    private navigationService = inject(NavigationService);
    private webWorkerService = inject(WebWorkerService);
    private router = inject(Router);
    // eslint-disable-next-line unicorn/numeric-separators-style
    private defaultPingInterval = 20000;
    private minPingInterval = 3000;
    private defaultContentPath = 'App-v1.0/OfflinePage/Content';
    isMaintenanceMode: boolean;
    async isEnabled(product: string): Promise<boolean> {
        const response: MaintenanceConfig = await firstValueFrom(this.http.get(`maintenance/${product}`));

        if (response?.contentPath) {
            this.localStorage.set<string>(LocalStoreKey.MaintenanceContent, response.contentPath);
        }

        if (response?.pingInterval !== undefined) {
            const pingInterval = response.pingInterval as number;
            this.localStorage.set<number>(LocalStoreKey.MaintenancePingInterval, pingInterval);
        }

        if (response?.statusCodesToCheck && response?.statusCodesToCheck.length > 0) {
            this.localStorage.set<number[]>(LocalStoreKey.MaintenanceInterceptorCodes, response?.statusCodesToCheck);
        }
        const enabled = response.enabled as boolean;
        this.localStorage.set<boolean>(LocalStoreKey.MaintenanceEnabled, enabled);
        return enabled;
    }

    async navigateToPath(url: string) {
        await this.navigationService.goTo(url);
    }

    async pingMaintenance() {
        const enabled = await this.isEnabled(this.currentProduct);
        if (enabled) {
            this.webWorkerService.removeWorker(WorkerType.MaintenanceCheckInterval);

            if (this.pingInterval > this.minPingInterval) {
                this.webWorkerService.createWorker(WorkerType.MaintenanceCheckInterval, { interval: this.pingInterval }, () => {
                    this.checkMaintenance();
                });
            }
        } else {
            this.webWorkerService.removeWorker(WorkerType.MaintenanceCheckInterval);
        }
    }

    async checkMaintenance() {
        const enabled = await this.isEnabled(this.currentProduct);

        const currentUrl = this.router.url;
        this.isMaintenanceMode = currentUrl.includes('maintenance');
        if (!enabled) {
            await this.gotoProduct();
        } else if (enabled && !this.isMaintenanceMode) {
            await this.goToMaintenance();
        }
    }
    async goToMaintenance() {
        await this.navigateToPath(`/${this.pageLanguage}/maintenance/${this.currentProduct}`);
    }
    async gotoProduct() {
        await this.navigateToPath(`/${this.pageLanguage}/${this.defaultAppPath}`);
    }
    get maintenanceIsEnabled(): boolean {
        return this.localStorage.get<boolean>(LocalStoreKey.MaintenanceEnabled) ?? false;
    }
    get statusCodes(): number[] {
        return this.localStorage.get<number[]>(LocalStoreKey.MaintenanceInterceptorCodes) ?? [0];
    }

    get contentPath() {
        return this.localStorage.get<string>(LocalStoreKey.MaintenanceContent) ?? this.defaultContentPath;
    }

    get pingInterval() {
        return this.localStorage.get<number>(LocalStoreKey.MaintenancePingInterval) || this.defaultPingInterval;
    }

    get defaultAppPath() {
        const lkp = this.lastKnownProductService.get();
        return (
            lkp.name === this.lastKnownProductService.UNKNOWN ? this.config.defaultPath.toLowerCase() : this.config.productPathMap[lkp.name]
        ).toLowerCase();
    }
    get pageLanguage() {
        return this.page.lang;
    }

    get currentProduct() {
        return this.getKeyFromValue(this.config.productPathMap as Record<string, string>, this.defaultAppPath);
    }

    private getKeyFromValue(config: Record<string, string>, value: string): string {
        const entry = Object.entries(config).find(([, val]) => val.toLowerCase() === value.toLowerCase());
        return entry ? entry[0] : value.toLowerCase();
    }
}
